import { useEffect } from 'react';
import { Checkbox } from '@components/common/Checkbox';
import { BoxItemList, FilterItem } from '@components/filter/FilterStyle';
import theme from '@theme/index';
import styled from 'styled-components';

/**
 * CheckboxGroupFilter - 체크박스 그룹 필터 컴포넌트
 *
 * 복수 선택이 가능한 체크박스 그룹 필터
 * 기존 Checkbox 공용 컴포넌트를 활용
 *
 * @param title - 필터 타이틀
 * @param options - 체크박스 옵션 목록 { key, title }
 * @param selected - 선택된 키 배열
 * @param setSelected - 선택 상태 변경 함수
 * @param initialValue - 초기값 (필터 초기화 시 사용)
 * @param description - 필터 설명
 */

interface Option {
  key: string;
  title: string;
}

interface Props {
  title: string;
  options: Option[];
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  initialValue?: string[];
  description?: string;
}

const CheckboxGroupFilter = ({
  title,
  options,
  selected,
  setSelected,
  initialValue,
  description,
}: Props) => {
  const allKeys = options.map((option) => option.key);
  const isAllSelected = allKeys.every((key) => selected.includes(key));

  const handleChange = (key: string) => {
    // 여러 개(또는 전체)가 선택된 상태에서 옵션 하나를 클릭하면 "이것만 보고 싶다"는
    // 의도로 보고 그 옵션만 단일 선택으로 좁힌다. 이후 다른 옵션을 클릭하면 거기에
    // 추가(다중 선택)되고, 단일 선택 상태에서 같은 옵션을 다시 누르면 일반 토글로 해제된다.
    if (selected.length > 1) {
      setSelected([key]);
    } else if (selected.includes(key)) {
      setSelected(selected.filter((item) => item !== key));
    } else {
      setSelected([...selected, key]);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(allKeys);
    }
  };

  const handleReset = () => {
    if (initialValue) {
      setSelected(initialValue);
    }
  };

  useEffect(() => {
    window.addEventListener('reset-filter', handleReset);
    return () => window.removeEventListener('reset-filter', handleReset);
  });

  return (
    <FilterItem
      title={title}
      description={description}
      content={
        <CheckboxList>
          <CheckboxItem onClick={handleSelectAll}>
            <input
              type="checkbox"
              checked={isAllSelected}
              readOnly
              onClick={(e) => e.stopPropagation()}
            />
            <span className="checkbox_icon" />
            <p>전체</p>
          </CheckboxItem>
          {options.map((option) => (
            <CheckboxItem key={option.key} onClick={() => handleChange(option.key)}>
              <input
                type="checkbox"
                checked={selected.includes(option.key)}
                readOnly
                onClick={(e) => e.stopPropagation()}
              />
              <span className="checkbox_icon" />
              <p>{option.title}</p>
            </CheckboxItem>
          ))}
        </CheckboxList>
      }
    />
  );
};

export default CheckboxGroupFilter;

const CheckboxList = styled(BoxItemList)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
`;

const CheckboxItem = styled(Checkbox)`
  cursor: pointer;
  color: ${theme.neutralGray[600]};
  margin-bottom: 0;
  display: flex;
  align-items: center;

  p {
    font-size: 0.875rem;
    user-select: text;
    margin: 0 0 0 0.5rem;
  }
`;
